import { modelState, ModelStateList } from "@/utils/modelState";
import { startOfDay } from 'date-fns';
import _ from "lodash";
import { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import { ReservationDiffToReservationDiff } from "./model.Diffs";
import { buildSaveReservationRequest, calculateDatePrice, getAmountAfterDiscountGross, getAmountBeforeDiscountGross } from "./model.Reservations";
import type { ModelContext } from "./model.context";
import { subscribeDates } from './model.datesAndProfiles';
import { RoomBlockDate, RoomBlockDateRoom } from './model.saveAndHistory';
import {
    CheckPriceResult
} from "./types";
export function initializePriceChecks(context: ModelContext) {
    context.checkRoomsWarning = () => {
        const roomBookings = context.reservation.RoomBookings.toArray();
        if (roomBookings.length === 0) {
            context.reservation.RoomsWarningText.setValue(null);
            return;
        }
        const sortedBookings = roomBookings.sort((a, b) => a.CheckIn.value.getDate() - b.CheckIn.value.getDate());
        let roomWarning = false;
        for (let i = 0; i < sortedBookings.length - 1; i++) {
            const current = sortedBookings[i];
            const next = sortedBookings[i + 1];
            if (current.CheckOut.value.getDate() > next.CheckIn.value.getDate()) {
                // Conflict found
                roomWarning = true;
                break;
            }
        }
        context.reservation.RoomsWarningText.setValue(roomWarning ? "There are overlapping dates in multiple room bookings." : null);
    };
    context.checkProgramsWarning = () => {
        const roomBookings = context.reservation.RoomBookings.toArray();
        const programBookings = context.reservation.ProgramBookings.toArray();
        if (roomBookings.length <= 0 || programBookings.length <= 0) {
            context.reservation.ProgramsWarningText.setValue(null);
            return;
        }
        const checkInDates = roomBookings.map(rb => startOfDay(rb.CheckIn.value));
        const checkOutDates = roomBookings.map(rb => startOfDay(rb.CheckOut.value));
        const arrivalDate = new Date(_.min([...checkInDates.map(d => d.getTime())]));
        const departureDate = new Date(_.max([...checkOutDates.map(d => d.getTime())]));
        const programStart = new Date(arrivalDate);
        programStart.setDate(programStart.getDate() + 1);
        let roomWarning = !(programBookings.every((event) => event.Start.value.getDate() === programStart.getDate() &&
            event.End.value.getDate() === departureDate.getDate()));
        context.reservation.ProgramsWarningText.setValue(roomWarning ? "The program dates do not match the room dates." : null);
    };
    context.checkPrices = async () => {
        context.checkPricesController.abort('cancelled');
        context.checkPricesController = new AbortController();
        context.reservation.IsPriceLoading.setValue(true);
        const roomBookings = context.reservation.RoomBookings.toArray();
        const programBookings = context.reservation.ProgramBookings.toArray();
        roomBookings.forEach(rb => {
            rb.Price.isLoading.setValue(true);
        });
        programBookings.forEach(pb => {
            pb.Price.isLoading.setValue(true);
        });
        const request = buildSaveReservationRequest(context.reservation);
        let result: CheckPriceResult;
        try {
            const response = await fetch(`/reservations-price-check`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify(request),
                signal: context.checkPricesController.signal
            });
            result = await response.json() as CheckPriceResult;
        }
        catch (ex) {
            if (ex === "cancelled" || ex.name === "AbortError") {
                return;
            }
            else {
                throw ex;
            }
        }
        context.reservation.IsPriceLoading.setValue(false);
        roomBookings.forEach(rb => {
            const room = result.rooms.find(r => r.reservationRoomId === rb.ReservationRoomId);
            if (room) {
                if (room.price) {
                    rb.Price.price.setValue(room.price);
                }
                const existingDates = [...rb.Dates];
                rb.Dates = room.dates.map(d => {
                    return {
                        Date: new Date(d.Date),
                        //BaseAmount: d.BaseAmount,
                        //PayableAmount: d.PayableAmount,
                        VoucherEffects: d.VoucherEffects,
                        PriceBreakdown: d.PriceBreakdown,
                        SegmentIndex: modelState(d.SegmentIndex),
                        RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(d.RatePlanOverride)),
                        BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(d.BaseRateOverride)),
                        RecalculateStayPeriod: modelState(null),
                        UseOriginalRatePlan: modelState(null),
                        MinorAdjustment: modelState(d.MinorAdjustment),
                        CalculatedPrice: calculateDatePrice(d.PriceBreakdown, (getAmountBeforeDiscountGross(d) / 100), (getAmountAfterDiscountGross(d) / 100), rb.NoCharge.value),
                        AppliedVouchers: modelState([]),
                        NoShow: modelState(d.NoShow),
                        NoCharge: modelState(d.NoCharge),
                        AdditionalCharges: modelState(d.AdditionalCharges),
                        PriceSourceDate: modelState<Date | null>(null),
                        EvaluationSessionJson: modelState<string | null>(null),
                        OpenPrice: modelState<number | null>(d.OpenPrice ?? null),
                        OpenPriceDescription: modelState<string | null>(d.OpenPriceDescription ?? null),
                        IgnoreVouchers: modelState<boolean | null>(d.IgnoreVouchers ?? null)
                    };
                });
                rb.Dates.forEach(d => {
                    const existing = existingDates.find(ed => ed.Date.getTime() === d.Date.getTime());
                    if (!existing) {
                        return;
                    }
                    d.RatePlanOverride.setValue(existing.RatePlanOverride.value);
                    d.BaseRateOverride.setValue(existing.BaseRateOverride.value);
                    d.RecalculateStayPeriod.setValue(existing.RecalculateStayPeriod.value);
                    d.UseOriginalRatePlan.setValue(existing.UseOriginalRatePlan.value);
                    d.MinorAdjustment.setValue(existing.MinorAdjustment.value);
                    d.AppliedVouchers.setValue(existing.AppliedVouchers.value);
                    d.OpenPrice.setValue(existing.OpenPrice.value);
                    d.OpenPriceDescription.setValue(existing.OpenPriceDescription.value);
                    d.IgnoreVouchers.setValue(existing.IgnoreVouchers.value);
                    d.PriceSourceDate.setValue(existing.PriceSourceDate?.value ?? null);
                    d.EvaluationSessionJson?.setValue(existing.EvaluationSessionJson?.value ?? null);
                });
                rb.DatesUIVersion.setValue(rb.DatesUIVersion.value + 1);
                rb.RoomMovements.setValue(room.roomMovements);
                rb.RoomBlocks.setValue(room.roomBlocks.map<RoomBlockDate>(rbd => ({
                    Date: startOfDay(new Date(rbd.Date)),
                    Rooms: new ModelStateList<RoomBlockDateRoom>(rbd.Rooms.map(r => ({
                        LocationId: r.LocationId,
                        IsAllDay: r.IsAllDay,
                        Start: new Date(r.Start),
                        End: new Date(r.End),
                        ReservationRoomId: r.ReservationRoomId
                    }))),
                    Version: modelState(0)
                })));
                if (room.availability) {
                    rb.AvailabilityCheckResult.setValue({
                        IsAvailable: room.availability.isAvailable,
                        IsBlockedByTempReservation: room.availability.isBlockedByTempReservation,
                        BlockedByTempReservationNumbers: room.availability.blockedByTempReservationNumbers
                    });
                }
                else {
                    rb.AvailabilityCheckResult.setValue({
                        IsAvailable: false,
                        IsBlockedByTempReservation: false,
                        BlockedByTempReservationNumbers: []
                    });
                }
                // Update additional charges if present
                if (room.additionalCharges) {
                    rb.AdditionalCharges = modelState(room.additionalCharges);
                }
                else {
                    rb.AdditionalCharges = modelState([]);
                }
            }
            rb.Price.isLoading.setValue(false);
        });
        programBookings.forEach(pb => {
            const program = result.programs.find(p => p.reservationProgramId === pb.ReservationProgramId);
            if (program) {
                if (program.price) {
                    pb.Price.price.setValue(program.price);
                }
                const existingDates = [...pb.Dates];
                pb.Dates = program.dates.map(d => {
                    return {
                        Date: new Date(d.Date),
                        VoucherEffects: d.VoucherEffects,
                        PriceBreakdown: d.PriceBreakdown,
                        SegmentIndex: modelState(d.SegmentIndex),
                        RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(d.RatePlanOverride)),
                        RecalculateStayPeriod: modelState(null),
                        BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(d.BaseRateOverride)),
                        UseOriginalRatePlan: modelState(null),
                        MinorAdjustment: modelState(d.MinorAdjustment),
                        CalculatedPrice: calculateDatePrice(d.PriceBreakdown, (getAmountBeforeDiscountGross(d) / 100), (getAmountAfterDiscountGross(d) / 100), pb.NoCharge.value),
                        AppliedVouchers: modelState([]),
                        NoShow: modelState(d.NoShow),
                        NoCharge: modelState(d.NoCharge),
                        AdditionalCharges: modelState(d.AdditionalCharges),
                        PriceSourceDate: modelState<Date | null>(null),
                        EvaluationSessionJson: modelState<string | null>(null),
                        OpenPrice: modelState<number | null>(d.OpenPrice ?? null),
                        OpenPriceDescription: modelState<string | null>(d.OpenPriceDescription ?? null),
                        IgnoreVouchers: modelState<boolean | null>(d.IgnoreVouchers ?? null)
                    };
                });
                pb.Dates.forEach(d => {
                    const existing = existingDates.find(ed => ed.Date.getTime() === d.Date.getTime());
                    if (!existing) {
                        return;
                    }
                    d.RatePlanOverride.setValue(existing.RatePlanOverride.value);
                    d.BaseRateOverride.setValue(existing.BaseRateOverride.value);
                    d.RecalculateStayPeriod.setValue(existing.RecalculateStayPeriod.value);
                    d.UseOriginalRatePlan.setValue(existing.UseOriginalRatePlan.value);
                    d.MinorAdjustment.setValue(existing.MinorAdjustment.value);
                    d.AppliedVouchers.setValue(existing.AppliedVouchers.value);
                    d.OpenPrice.setValue(existing.OpenPrice.value);
                    d.OpenPriceDescription.setValue(existing.OpenPriceDescription.value);
                    d.IgnoreVouchers.setValue(existing.IgnoreVouchers.value);
                    d.PriceSourceDate.setValue(existing.PriceSourceDate?.value ?? null);
                    d.EvaluationSessionJson?.setValue(existing.EvaluationSessionJson?.value ?? null);
                });
                pb.DatesUIVersion.setValue(pb.DatesUIVersion.value + 1);
                // Update availability check result for programs
                if (program.availability) {
                    pb.AvailabilityCheckResult.setValue({
                        IsAvailable: program.availability.isAvailable,
                        IsBlockedByTempReservation: program.availability.isBlockedByTempReservation,
                        BlockedByTempReservationNumbers: program.availability.blockedByTempReservationNumbers
                    });
                }
                else {
                    pb.AvailabilityCheckResult.setValue({
                        IsAvailable: true,
                        IsBlockedByTempReservation: false,
                        BlockedByTempReservationNumbers: []
                    });
                }
            }
            pb.Price.isLoading.setValue(false);
        });
        subscribeDates(context.reservation);
        context.reservation.OrderDiffs.setValue(result.orderDiffs.map(d => ReservationDiffToReservationDiff(d)));
    };
}
